import { BaseDocumentFields, UID } from '../general-models';
import { NewgenusTimestamp } from '../shared-models/timestamp';
import { OrganizationSettings } from './organization-models';

/**
 * Source mode chosen on Step 1 of the org-creation wizard.
 * See `docs/features/CREATE-ORGANIZATION-WIZARD-PLAN.md` §4.3 Step 1 and D15.
 */
export type OrgDraftSourceMode = 'blank' | 'scaffold' | 'template' | 'clone' | 'excel';

/**
 * Lifecycle status of an `orgDrafts/{draftKey}` document.
 * - `in-progress` — user is still working through the wizard.
 * - `submitted`   — `createFromDraft` succeeded; the draft is archived (kept briefly for audit, then pruned).
 * - `partial-recovery` — `createFromDraft` failed mid-flight; the draft holds enough state to resume.
 * - `abandoned`   — user explicitly trashed the draft from the drafts picker.
 */
export type OrgDraftStatus = 'in-progress' | 'submitted' | 'partial-recovery' | 'abandoned';

/**
 * 1..6 — matches `OrgCreationWizardComponent` step indices.
 * Step 1 Source · 2 Basics · 3 Structure · 4 Staff · 5 Plan & Features · 6 Review.
 */
export type OrgDraftStep = 1 | 2 | 3 | 4 | 5 | 6;

/**
 * Stages reported by the backend `createFromDraft` orchestrator (§15.4 of the plan).
 * Drives the live progress bar on the submit screen.
 */
export type OrgDraftProgressStage =
    | 'org'
    | 'features'
    | 'policies'
    | 'parts'
    | 'security-groups'   // v2: template SG snapshot apply (template/clone with SGs in scope)
    | 'hats'
    | 'apply-hats'        // v2: overwrite bulkAddParts auto-hats with template hat shapes
    | 'staff'
    | 'finalising'
    | 'done'
    | 'error';

export interface OrgDraftProgress {
    stage: OrgDraftProgressStage;
    current: number;
    total: number;
    message?: string;
    /** Cloud function attempt number — useful for distinguishing retries from initial run. */
    attempt?: number;
    updatedAt?: NewgenusTimestamp;
}

/**
 * Variants offered when the user picks "Rudimentary scaffold" on Step 1 (D15).
 */
export type ScaffoldVariant = 'minimal' | 'with-executive-team';

/**
 * Reference to whatever the draft was seeded from. All fields are optional and
 * mutually-exclusive per `sourceMode`.
 */
export interface OrgDraftSourceRef {
    templateKey?: string;
    clonedFromOrgKey?: string;
    scaffoldVariant?: ScaffoldVariant;
    excelImportedAt?: NewgenusTimestamp;
}

/**
 * Per-row state for the Step 4 staff table.
 * Status is set by the client after the debounced `checkEmail` call lands.
 */
export type StaffResolutionStatus = 'unresolved' | 'existing-user' | 'invitable' | 'invalid';

export type StaffRole = 'owner' | 'manager' | 'member';

export interface StaffRow {
    /** Stable client-generated key so the row survives reordering / undo / Excel round-trip. */
    rowId: string;
    email: string;
    firstName?: string;
    lastName?: string;
    /**
     * `partKey`s of the posts the user is assigned to. A single person can hold
     * multiple posts (e.g. a deputy who also chairs a committee). Empty array
     * means "no post yet" and the row will be skipped by the staff-stage
     * processor (§6.5 gate). Step 4's UI mirrors the New Hat dialog: searchable
     * multi-select with selected posts surfaced as chips above the search box.
     */
    postKeys?: string[];
    /**
     * @deprecated Use `postKeys`. Kept on the model for backward compatibility
     * with drafts persisted before the multi-post change — the backend reads
     * `postKeys ?? (postKey ? [postKey] : [])` so legacy single-post drafts
     * still process correctly.
     */
    postKey?: string;
    role?: StaffRole;
    resolvedUid?: UID;
    resolutionStatus: StaffResolutionStatus;
    resolutionCheckedAt?: NewgenusTimestamp;
}

/**
 * Structural part captured in a draft. Mirrors the live `OrgPart` shape minus
 * the runtime-derived fields. The wizard writes these into the draft and
 * `createFromDraft` converts them into `bulkAddParts` command-doc payloads.
 *
 * In P1 only the fields below are required; the draft preserves any extra
 * fields the user sets (colour, vfp, etc.) verbatim.
 */
export interface OrgDraftPart {
    /** Client-generated key. Becomes the final `partKey` after submit. */
    partKey: string;
    name: string;
    partType: 'executive' | 'division' | 'department' | 'section' | 'unit' | 'post';
    /** Empty string for root. */
    parentPartKey: string;
    /** Insertion order within the parent. */
    id: number;
    depth: number;
    vfp?: string;
    headerRole?: 'head' | 'deputy' | 'assistant';
    /**
     * Singleton (org-wide) flag set by Step 3's crown 👑 toggle to mark
     * the post that the org creator's CEO hat will be attached to.
     *
     * The wizard enforces "at most one part has this true" via
     * `OrgWizardStateService.setCeoPost`. The backend orchestrator's
     * `findCeoPost` uses this as its priority-1 signal (lowest-depth post
     * is the safety-net fallback for legacy/unflagged drafts).
     *
     * NOT to be confused with `headerRole === 'head'` — that is a
     * MULTI-instance UI field (each department/division can have its own
     * head/deputy/assistant). Conflating the two caused the
     * Flash-Components-CEO-on-the-wrong-post bug; see plan addendum 19.
     */
    isCeoPost?: boolean;
    [extra: string]: unknown;
}

/**
 * Draft document persisted at `orgDrafts/{draftKey}` — see plan §5.1.
 *
 * - Owned by `createdBy`. Per-user security rules.
 * - At most 3 `in-progress` drafts per user (D11), enforced both client- and server-side.
 * - Auto-deleted 30 days after `createdOn` (D10).
 */
export interface OrgDraft extends BaseDocumentFields {
    key: string;
    createdBy: UID;
    status: OrgDraftStatus;
    currentStep: OrgDraftStep;

    /** `createdOn + 30 days`. The daily prune scheduled function uses this. */
    expiresOn: NewgenusTimestamp;

    sourceMode: OrgDraftSourceMode;
    sourceRef?: OrgDraftSourceRef;

    basics: {
        name: string;
        description: string;
        settings: Partial<OrganizationSettings>;
    };

    structure: OrgDraftPart[];
    staff: StaffRow[];
    features: string[];

    /** Populated by the backend during submit; surfaces on the wizard's submit screen. */
    progress?: OrgDraftProgress;

    /**
     * If submit fails partway, the partially-written org's key is recorded here so
     * a retry can resume from the failed stage instead of starting fresh.
     */
    partialOrgKey?: string;

    /**
     * Resumable plan for the chunked `bulkAddParts` submit (plan addendum 17).
     * Populated by the orchestrator when a draft has more parts than the
     * per-task chunk cap (50). On retry the orchestrator skips already-
     * completed chunks. `undefined` for small drafts that fit in a single
     * chunk (the orchestrator skips planning entirely).
     */
    chunkPlan?: OrgDraftChunkPlan;

    /**
     * Transient per-step UI snapshot. Persisted alongside the draft so view-
     * only state survives page refreshes (P1 only uses the `structureTree`
     * slot — the Step 3 OrgPartTreeBuilder's per-node `dirty`/`moved`/`isNew`/
     * `_originalParentPartKey`/`_originalIndex`/`_original`/`colour` markers
     * which don't live in the flattened `structure: OrgDraftPart[]`).
     *
     * Backend has no opinion on the shape — it round-trips it verbatim.
     */
    uiSnapshot?: { structureTree?: unknown };

    /**
     * Persisted undo/redo history so the wizard's top-bar Undo/Redo continues
     * to work across page refreshes. The wizard maintains a per-step history
     * map (so undoing on Step 3 can never reach into Step 2's edits) and
     * round-trips that map through this field. Backend has no opinion on the
     * shape — it persists it verbatim. Cleared on submit so the live org
     * doesn't carry wizard scratch state.
     */
    history?: Record<string, unknown>;

    /**
     * Phase B (staff stage): when `true`, the orchestrator's staff stage will
     * trigger invitation emails for `invitable` rows (and any future email-bearing
     * paths). When `false` or undefined, invitation docs are still created in
     * `pending` state but no email goes out — admin can later trigger sends from
     * the org-board invitations panel via `api-invitationsResend-default`.
     *
     * Default: `false` in Phase B (avoid spamming testers). Phase C flips the
     * default to `true` and introduces rate limiting.
     *
     * Set by the Step 6 review screen's `mat-slide-toggle`.
     */
    autoSendInvitations?: boolean;

    /**
     * Phase B (staff stage): per-row outcome report populated by the orchestrator's
     * staff stage. Read by the wizard's submit screen to show "47 of 50 staff
     * staged. 3 failed: [email1: reason, …]" feedback to the org creator.
     *
     * `undefined` while the stage hasn't run yet (or for drafts created before
     * Phase B). Cleared on a fresh retry.
     */
    staffStageReport?: StaffStageReport;

    /**
     * v2 — extra snapshot data attached when `sourceMode === 'template'` or
     * `'clone'`. Carries the hats / SGs / policies the orchestrator's
     * apply stages will write into the new org on submit (see
     * TEMPLATE-V2-PLAN.md §2-§3). Absent for drafts whose source is just
     * structure (v1 templates, scaffold, blank, excel).
     */
    snapshotPayload?: OrgDraftSnapshotPayload;
}

/**
 * Phase B (staff stage): summary of what happened to each staff row during the
 * orchestrator's staff stage. Persisted on `OrgDraft.staffStageReport` and shown
 * on the wizard's submit screen.
 */
export interface StaffStageReport {
    /** When the staff stage ran (last attempt — overwritten on retry). */
    completedAt: NewgenusTimestamp;
    /** Total rows processed across all outcomes (sum of arrays below). */
    totalRows: number;
    /** One entry per `StaffRow` from `draft.staff`. Order matches the draft. */
    rows: StaffStageRowOutcome[];
}

/**
 * Outcome for a single staff row inside `StaffStageReport`. Outcome semantics:
 *
 * - `invitation-created`: fresh `invitations/` doc was written (and for
 *   `existing-user` rows, the matching `pendingApprovals` + `pendingAssignedToArray`
 *   pre-stage writes were applied).
 * - `skipped-already-invited`: an existing pending invitation already covers
 *   this (email, orgKey, hatKeys) tuple — reused instead of creating a duplicate.
 * - `skipped-already-staged`: pre-stage records for this (uid, hatKeys) tuple
 *   already exist (deterministic id collision on re-run of the same draft).
 *   Idempotent no-op — counts as success.
 * - `skipped-unresolved`: row was `unresolved` or `invalid` at submit time
 *   (Step 4 validation gate should have caught this — defence in depth).
 * - `failed`: per-row error during write. `reason` carries the message; the
 *   stage continues with the next row (fail-soft per §8.6).
 */
export interface StaffStageRowOutcome {
    rowId: string;
    email: string;
    outcome:
        | 'invitation-created'
        | 'skipped-already-invited'
        | 'skipped-already-staged'
        | 'skipped-unresolved'
        | 'failed';
    /** Set when `outcome === 'invitation-created' | 'skipped-already-invited'`. */
    invitationKey?: string;
    /** Set when a `pendingApprovals` doc was created or matched (existing-user only). */
    pendingApprovalKey?: string;
    /** Set when `outcome === 'failed'`. Human-readable. */
    reason?: string;
}

/**
 * Tier of data a template snapshot can carry. Locked literal union so request
 * validation can reject typos.
 *
 * `structure` is the only required tier — every snapshot includes parts.
 * Other tiers are opt-in per publish (§10 of TEMPLATE-V2-PLAN.md).
 */
export type OrgTemplateScope =
    | 'structure'
    | 'features'
    | 'settings'
    | 'hats'
    | 'securityGroups'
    | 'policies';

/**
 * Stripped hat shape carried in a v2 template snapshot. User-assignment fields
 * (`assignedToArray`, `pendingAssignedToArray`, `userKeys`, `assignedTo`,
 * `assigned`) are intentionally absent at the type level so they cannot leak
 * through serialisation. Position links (`positionArray`) and SG links
 * (`securityGroupsArray`) reference snapshot keys verbatim — Firestore
 * subcollections are scoped by org, so cross-org collisions are impossible.
 */
export interface OrgHatTemplate {
    key: string;
    name: string;
    description?: string;
    /** Post part-keys this hat anchors. Matches `snapshot.parts[].partKey`. */
    positionArray: string[];
    /** Security-group keys this hat carries. Matches `snapshot.securityGroups[].key`. */
    securityGroupsArray: string[];
    /** Per-hat policy bindings — keys match `snapshot.policies[].key`. */
    policyKeys?: Record<string, string>;
    isMultiUsers?: boolean;
    /**
     * Marks the org-wide CEO hat (the one anchored to the post with
     * `isCeoPost === true`). Used by the orchestrator's apply-hats stage to
     * skip the default `writeCeoHat` write when the template already carries
     * the CEO hat shape.
     */
    isCeoHat?: boolean;
}

/**
 * Stripped security-group shape for a v2 template snapshot. User-assignment
 * fields (`assignedTo`, `assigned`) intentionally absent.
 */
export interface OrgSecurityGroupTemplate {
    key: string;
    name: string;
    description?: string;
    features: string[];
    /** OrgSecurityGroupFeatureDetails shape — kept as `unknown` to avoid
     *  pulling the wider organization-models tree into common-creation. */
    featureDetails: Record<string, unknown>;
    /** Policy-key references — match `snapshot.policies[].key`. */
    policies?: string[];
}

/**
 * Stripped policy shape for a v2 template snapshot. Authorship fields
 * (`createdBy`, `modifiedBy`) intentionally absent — author identity from the
 * source org isn't meaningful in the destination.
 */
export interface OrgPolicyTemplate {
    key: string;
    name: string;
    description?: string;
    /** PolicyCategory enum value — kept loose to avoid cross-lib import. */
    category: string;
    customCategory?: string;
    version: number;
    /** Rich-text / HTML policy body. */
    policyWriting: string;
    /** PolicyStatus enum value — kept loose to avoid cross-lib import. */
    status: string;
    isPublished?: boolean;
    effectiveDate?: NewgenusTimestamp;
}

/**
 * Curated org templates — `orgTemplates/{templateKey}`. Snapshots only; no users.
 *
 * Snapshot evolution:
 * - v1 (legacy): parts + features + settings only.
 * - v2 (current): adds optional `hats` / `securityGroups` / `policies` per
 *   the publisher's `snapshotScope` selection (see TEMPLATE-V2-PLAN.md §10).
 *   Readers honour `snapshotVersion` and fail-soft to v1 behaviour when
 *   absent or set to `1`.
 */
export interface OrgTemplate extends BaseDocumentFields {
    key: string;
    name: string;
    description: string;
    visibility: 'system' | 'org-private' | 'shared';
    ownerOrgKey?: string;
    sharedWithOrgKeys?: string[];
    version: number;
    /**
     * Snapshot shape version — bumped on non-additive changes. Absent on
     * legacy v1 docs; readers treat absent as `1`.
     */
    snapshotVersion?: 1 | 2;
    /**
     * Exactly which tiers the publisher opted in to. Always includes
     * `'structure'`. Absent on v1 docs; readers treat absent as
     * `['structure', 'features', 'settings']` (v1 behaviour).
     */
    snapshotScope?: OrgTemplateScope[];
    snapshot: {
        parts: OrgDraftPart[];
        features: string[];
        settings: Partial<OrganizationSettings>;
        // v2 additions — present iff their key is in `snapshotScope`.
        hats?: OrgHatTemplate[];
        securityGroups?: OrgSecurityGroupTemplate[];
        policies?: OrgPolicyTemplate[];
    };
}

/**
 * Request body for the `api-orgCreation-createFromDraft` callable.
 * Server reads the draft from Firestore — the client only sends the `draftKey`.
 */
export interface CreateFromDraftRequest {
    draftKey: string;
}

export interface CreateFromDraftResponse {
    orgKey: string;
}

export interface CreateFromDraftError {
    /** Which stage failed — surfaces in the wizard's error toast / partial-recovery UI. */
    stage: OrgDraftProgressStage;
    chunkIndex?: number;
    partialOrgKey?: string;
    retryable: boolean;
    message: string;
}

/**
 * Request body for the `api-orgCreation-publishOrgAsTemplate` callable (P4.c).
 *
 * Caller must be an owner of `orgKey` (checked server-side against
 * `organizations/{orgKey}.owners[]`). The server snapshots the org's parts +
 * features + settings into `orgTemplates/{key}`, marking visibility per the
 * caller's selection.
 *
 * `templateKey` is optional — when supplied (and the caller still owns the
 * org), the existing template is overwritten in place and its `version` is
 * incremented. When absent, a new template doc is created.
 */
export interface PublishOrgAsTemplateRequest {
    orgKey: string;
    name: string;
    description: string;
    visibility: 'org-private' | 'shared';
    /** Required when `visibility === 'shared'`. Each entry must be an orgKey
     *  the caller can read; the server does not enforce cross-org consent in
     *  v1 (the recipients see the template in their inbox and choose to use
     *  it or ignore it). */
    sharedWithOrgKeys?: string[];
    /** When set, overwrite the existing template (must be owned by `orgKey`). */
    templateKey?: string;
    /**
     * v2 — which tiers the publisher opts in to. Always written as
     * `['structure', ...selected]` server-side; `'structure'` is locked-on.
     * Absent (or empty) → server defaults to `['structure', 'features',
     * 'settings']` matching v1 behaviour.
     *
     * Dependency warnings (hats-without-SGs, etc.) are surfaced in the FE
     * publish dialog only — the server accepts whatever scope the publisher
     * confirmed and the importer drops dangling refs defensively.
     */
    scope?: OrgTemplateScope[];
}

export interface PublishOrgAsTemplateResponse {
    templateKey: string;
    version: number;
    /** Echo of the resolved scope (after server defaults applied). */
    scope: OrgTemplateScope[];
}

/**
 * v2 — extra snapshot payload threaded onto a draft when the source mode is
 * `'template'` or `'clone'`. Carries the hats / SGs / policies the orchestrator's
 * apply stages will write into the new org on submit.
 *
 * Absent on:
 *  - Drafts created before v2 shipped.
 *  - `blank` / `scaffold` / `excel` source modes (no source to snapshot).
 *  - `template` / `clone` source modes whose source only carries v1 data.
 *
 * Fields present iff the source's scope included that tier.
 */
export interface OrgDraftSnapshotPayload {
    snapshotVersion: 2;
    scope: OrgTemplateScope[];
    hats?: OrgHatTemplate[];
    securityGroups?: OrgSecurityGroupTemplate[];
    policies?: OrgPolicyTemplate[];
}

/** Maximum concurrent `in-progress` drafts a user may hold (D11). */
export const ORG_DRAFT_MAX_IN_PROGRESS_PER_USER = 3;

/** Days until a draft auto-expires (D10). */
export const ORG_DRAFT_RETENTION_DAYS = 30;

/**
 * Hard cap on parts in a single draft (plan §13, §15.2 rule 7).
 *
 * Default value for the wizard. Can be overridden at runtime by writing
 * `systemConfig/orgWizardLimits.maxParts` in Firestore — see
 * `OrgWizardStateService` (frontend) and `OrgCreationOrchestrator`
 * (backend) for the loader logic.
 */
export const ORG_DRAFT_MAX_PARTS = 1000;

/** Hard cap on staff rows in v1, per plan §15.2 rule 7. */
export const ORG_DRAFT_MAX_STAFF = 200;

/**
 * Per-chunk status inside an `OrgDraftChunkPlan`. Chunks transition
 * `pending → emitted → completed` (happy path) or `pending|emitted → failed`
 * (timeout / processor error). Plan addendum 17 (chunked submit).
 */
export type OrgDraftChunkStatus = 'pending' | 'emitted' | 'completed' | 'failed';

/** A single chunk inside an `OrgDraftChunkPlan`. */
export interface OrgDraftChunkPlanEntry {
    /** 0-based position in `OrgDraftChunkPlan.chunks`. */
    index: number;
    /** Parent under which this chunk's roots are planted. `orgKey` for the
     *  top-level wave; otherwise the partKey of a part planted by an earlier
     *  chunk (DFS slicing guarantees the anchor exists by the time this
     *  chunk runs). */
    anchorPartKey: string;
    /** Pre-minted partKeys this chunk plants (length ≤ `chunkMax`). Used
     *  by the orchestrator to confirm completion (poll until all keys
     *  appear in `organizations/{orgKey}/parts`). */
    partKeys: string[];
    /** Deterministic command-doc id (`bulk-add-${jobId}-${index}`) so
     *  retries overwrite the same doc instead of creating duplicates. */
    commandDocId: string;
    status: OrgDraftChunkStatus;
    emittedAt?: NewgenusTimestamp;
    completedAt?: NewgenusTimestamp;
    error?: string;
}

/**
 * Resumable execution plan for a >50-part `bulkAddParts` submit. Stored as
 * `orgDrafts/{draftKey}.chunkPlan` so a retried orchestrator run can skip
 * already-completed chunks. See plan addendum 17 for the algorithm.
 */
export interface OrgDraftChunkPlan {
    /** Unique per orchestrator-run; reused on resume so command-doc ids are
     *  stable across retries (idempotency). */
    jobId: string;
    /** Target org being built. */
    orgKey: string;
    /** Per-chunk part cap frozen at planning time (currently 50 — Firestore
     *  500-write batch budget at ~5 writes per part). */
    chunkMax: number;
    chunks: OrgDraftChunkPlanEntry[];
    /** Sum of `partKeys.length` over completed chunks — drives the wizard
     *  progress bar without re-summing on every read. */
    completedCount: number;
    /** Total parts in the original draft (= `draft.structure.length`). */
    totalCount: number;
    startedAt: NewgenusTimestamp;
    updatedAt: NewgenusTimestamp;
    overallStatus: 'in-progress' | 'completed' | 'failed';
}
